import React, { useMemo } from "react";

function clamp(v) { return Math.max(0, Math.min(100, v|0)); }

export default function VisualPanel({ visual }) {
  const stakeholders = useMemo(() => visual?.stakeholders || [], [visual]);
  const powerHolders = useMemo(() => new Set((visual?.power_holders || []).map(s => s.toLowerCase().trim())), [visual]);

  const pressure = clamp(visual?.pressure_level ?? 50);
  const bias = clamp(visual?.bias_level ?? 50);

  return (
    <div className="pisa-card p-7 md:p-9" data-testid="visual-panel">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-base font-medium text-[#1A1A1A]">System view</h3>
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#666666]">Snapshot</span>
      </div>

      {/* Stakeholders & power */}
      <div className="mb-7">
        <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-3">Stakeholders</p>
        <div className="flex flex-wrap gap-2" data-testid="visual-stakeholders">
          {stakeholders.length === 0 ? (
            <span className="text-sm text-[#666666]">—</span>
          ) : stakeholders.map((s, i) => {
            const isPower = powerHolders.has(s.toLowerCase().trim());
            return (
              <span
                key={i}
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  isPower
                    ? "bg-[#f4b847]/15 text-[#1A1A1A] border-[#f4b847]"
                    : "bg-[#2596be]/8 text-[#1A1A1A] border-[#2596be]/20"
                }`}
                data-testid={`stakeholder-chip${isPower ? "-power" : ""}`}
              >
                {isPower && <span className="mr-1.5 text-[#f4b847]">●</span>}
                {s}
              </span>
            );
          })}
        </div>
        {powerHolders.size > 0 && (
          <p className="text-xs text-[#666666] mt-3">
            <span className="inline-block h-2 w-2 rounded-full bg-[#f4b847] mr-2 align-middle" />
            Highlighted = where power currently sits.
          </p>
        )}
      </div>

      {/* Indicators */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-7">
        <div data-testid="indicator-pressure">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-xs uppercase tracking-[0.2em] text-[#666666]">Pressure</span>
            <span className="text-sm font-medium text-[#1A1A1A]">{pressure}<span className="text-[#666666]">/100</span></span>
          </div>
          <div className="pisa-bar"><span style={{ width: `${pressure}%`, background: "#e93e36" }} /></div>
        </div>
        <div data-testid="indicator-bias">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-xs uppercase tracking-[0.2em] text-[#666666]">Bias lean</span>
            <span className="text-sm font-medium text-[#1A1A1A]">{bias}<span className="text-[#666666]">/100</span></span>
          </div>
          <div className="pisa-bar"><span style={{ width: `${bias}%`, background: "#2596be" }} /></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-3">Assumptions</p>
          <ul className="space-y-2" data-testid="visual-assumptions">
            {(visual?.assumptions || []).map((a, i) => (
              <li key={i} className="text-sm text-[#1A1A1A] flex gap-2"><span className="text-[#f4b847] mt-0.5">·</span>{a}</li>
            ))}
            {(!visual?.assumptions || visual.assumptions.length === 0) && <li className="text-sm text-[#666666]">—</li>}
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-3">Signals to watch</p>
          <ul className="space-y-2" data-testid="visual-signals">
            {(visual?.signals || []).map((s, i) => (
              <li key={i} className="text-sm text-[#1A1A1A] flex gap-2"><span className="text-[#2596be] mt-0.5">·</span>{s}</li>
            ))}
            {(!visual?.signals || visual.signals.length === 0) && <li className="text-sm text-[#666666]">—</li>}
          </ul>
        </div>
      </div>

      {visual?.next_move && (
        <div className="mt-7 pt-6 border-t border-[#E5E5E5]">
          <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-2">Next move</p>
          <p className="text-base text-[#1A1A1A]" data-testid="visual-next-move">{visual.next_move}</p>
        </div>
      )}
    </div>
  );
}
